/**
 * units.ts — metric ↔ imperial conversion maths for the Step-0 capture.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.1 (patient journey).
 *
 * WHY: the journey stores height/weight canonically in cm/kg (the contract
 * shape), but UK patients often think in ft-in and st-lb. These helpers are
 * the single source of conversion truth: the capture toggle, the projection
 * chart's kg↔st display and the tests all use them. Constants are the exact
 * legal definitions (1 in = 2.54 cm, 1 lb = 0.45359237 kg, 1 st = 14 lb);
 * display rounding happens here so round-trips stay within one display unit.
 */

const CM_PER_INCH = 2.54;
const KG_PER_POUND = 0.45359237;
const POUNDS_PER_STONE = 14;

/** Round to one decimal place (display precision for kg and st values). */
export function round1(n: number): number {
  return Math.round(n * 10) / 10;
}

/** Height ft+in → whole cm (nearest cm — the capture's canonical unit). */
export function ftInToCm(feet: number, inches: number): number {
  return Math.round((feet * 12 + inches) * CM_PER_INCH);
}

/**
 * Height cm → ft+in. Total inches are rounded FIRST, then split, so the
 * inches component can never round up to 12 and leak an invalid "5 ft 12 in".
 */
export function cmToFtIn(cm: number): { feet: number; inches: number } {
  const totalInches = Math.round(cm / CM_PER_INCH);
  return { feet: Math.floor(totalInches / 12), inches: totalInches % 12 };
}

/** Weight st+lb → kg, one decimal place. */
export function stLbToKg(stones: number, pounds: number): number {
  return round1((stones * POUNDS_PER_STONE + pounds) * KG_PER_POUND);
}

/**
 * Weight kg → st+lb. Total pounds are rounded first (same guard as height:
 * the pounds component can never carry to 14).
 */
export function kgToStLb(kg: number): { stones: number; pounds: number } {
  const totalPounds = Math.round(kg / KG_PER_POUND);
  return {
    stones: Math.floor(totalPounds / POUNDS_PER_STONE),
    pounds: totalPounds % POUNDS_PER_STONE,
  };
}

/** Weight kg → decimal stones (chart display), one decimal place. */
export function kgToSt(kg: number): number {
  return round1(kg / KG_PER_POUND / POUNDS_PER_STONE);
}
